import { getInitials } from '../../../utility/Utils'
import moment from 'moment'

export const isInactiveTeamMember = (status?: string | null): boolean =>
  status?.trim().toLowerCase() === 'inactive'

export const getVisibleTeamMembers = <T extends { status?: string | null }>(
  members?: T[] | null
): T[] =>
  Array.isArray(members) ? members.filter((member) => !isInactiveTeamMember(member?.status)) : []

const getOwnerAvatars = (teamOwners: any) => {
  const avatars = teamOwners?.map((owner: any) => {
    return {
      title: owner.name || owner.displayName,
      content: owner.profilePictureBase64Encoded || getInitials(owner.name || owner.displayName),
      className: 'dsm-avatar-brand'
    }
  })
  return avatars
}

const downloadFileFromBlob = (data: any, isNotPdf: boolean, fileName: string) => {
  const blob = new Blob([data], {
    type: isNotPdf ? data?.type || 'application/vnd.ms-excel' : 'application/pdf'
  })
  const objectURL = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objectURL
  if (isNotPdf) {
    a.download = `${fileName.replace(/\.[^/.]+$/, '')}.xlsx`
  } else {
    a.download = fileName
  }
  a.click()
  a.remove()
  URL.revokeObjectURL(objectURL)
}

/**
 * Helper function to check if a document is stuck in processing
 * A document is considered stuck if it's in an in-progress status for >1 hour
 * Uses moment.js for UTC time comparison
 */
export const isDocumentStuck = (status: string, uploadTime: string): boolean => {
  const inProgressStatuses = [
    'Uploading',
    'Uploaded',
    'Ingested',
    'Analyzed',
    'Chunked',
    'Embedded'
  ].map((s) => s.toLowerCase())

  if (!status || !inProgressStatuses.includes(status.toLowerCase())) return false

  try {
    if (!uploadTime) return false
    const inputTime = moment.utc(uploadTime)
    const oneHourAgo = moment.utc().subtract(1, 'hour')
    return inputTime.isBefore(oneHourAgo)
  } catch (error) {
    console.error('Error calculating time difference:', error)
    return false
  }
}

export type TeamStatusIcon = 'pending' | 'ban' | 'triangle'

export interface TeamStatusMeta {
  label: string
  color: string
  icon: TeamStatusIcon
  /** Whether the team is not yet accessible (navigation/click should be disabled). */
  isBlocking: boolean
}

/**
 * Single source of truth for team request/lifecycle status → display meta.
 * Add new statuses here only - components should never hardcode a status string
 * or a new Badge/icon inline. Returns null for the happy path ('Active'), so no
 * indicator renders and the card/row is simply clickable with no extra clutter.
 */
export const getTeamStatusMeta = (status?: string | null): TeamStatusMeta | null => {
  switch (status?.toLowerCase()) {
    case 'pendingadminapproval':
      return {
        label: 'Pending approval',
        color: 'var(--status-pending)',
        icon: 'pending',
        isBlocking: true
      }
    case 'rejected':
      return {
        label: 'Request rejected',
        color: 'var(--status-rejected)',
        icon: 'ban',
        isBlocking: true
      }
    case 'failed':
      return {
        label: 'Request failed',
        color: 'var(--status-failed)',
        icon: 'triangle',
        isBlocking: true
      }
    default:
      return null
  }
}

export { getOwnerAvatars, downloadFileFromBlob }
